import { PinIcon } from "@wags/ui/product-icons.tsx";
import { ResponsiveSheet } from "@wags/ui/responsive-sheet.tsx";
import { BandTextButton } from "./provider-band-text-button";
import {
    countPlaces,
    formatAlsoIn,
    formatLocationsCaption,
    groupPlacesByState,
    leadPlace,
    type ProviderLocationInput,
    type ProviderLocationSummary,
    stateDisplayName,
    summarizeProviderLocations,
} from "./provider-locations";

const SHOW_ALL_LOCATIONS_MIN = 5;

function LocationsSheetBody({ summary }: { summary: ProviderLocationSummary }) {
    return (
        <>
            {summary.primary && (
                <div className="mb-5 flex items-center gap-3 rounded-nested bg-labrador-yellow/15 p-3">
                    <PinIcon
                        aria-hidden
                        className="size-5 text-doberman-black"
                    />
                    <div>
                        <p className="text-body font-semibold text-doberman-black">
                            {summary.primary.label}
                        </p>
                        <p className="text-body-small text-doberman-black/60">
                            Primary location
                        </p>
                    </div>
                </div>
            )}
            <dl className="space-y-3">
                {groupPlacesByState(summary.others).map(({ state, cities }) => (
                    <div
                        key={state ?? ""}
                        className="flex items-baseline gap-4"
                    >
                        <dt className="w-26 shrink-0 text-body-small font-medium text-doberman-black/50 md:w-32">
                            {state ? stateDisplayName(state) : "Other"}
                        </dt>
                        <dd className="min-w-0 flex-1 text-body text-doberman-black">
                            {cities
                                .map((city) => city || "Statewide")
                                .join(", ")}
                        </dd>
                    </div>
                ))}
            </dl>
        </>
    );
}

function LocationsSheet({ summary }: { summary: ProviderLocationSummary }) {
    return (
        <ResponsiveSheet
            title="Locations"
            description={formatLocationsCaption(summary)}
            trigger={<BandTextButton>Show all locations</BandTextButton>}
        >
            <LocationsSheetBody summary={summary} />
        </ResponsiveSheet>
    );
}

function AlsoIn({
    summary,
    shownLabel,
}: {
    summary: ProviderLocationSummary;
    shownLabel: string;
}) {
    const alsoIn = formatAlsoIn(summary, shownLabel);
    if (!alsoIn) return null;
    return (
        <div className="mt-1.5">
            <p className="text-body text-pretty text-doberman-black/60">
                {alsoIn}
            </p>
            {countPlaces(summary) >= SHOW_ALL_LOCATIONS_MIN && (
                <LocationsSheet summary={summary} />
            )}
        </div>
    );
}

function LocationsColumn({ summary }: { summary: ProviderLocationSummary }) {
    const lead = leadPlace(summary)?.label ?? "";
    return (
        <>
            <p className="flex flex-wrap items-center gap-2 text-body font-medium text-doberman-black">
                {lead}
                {summary.primary && summary.others.length > 0 && (
                    <span className="rounded-label bg-labrador-yellow/25 px-2 py-0.5 text-caption font-semibold">
                        Primary
                    </span>
                )}
            </p>
            <AlsoIn summary={summary} shownLabel={lead} />
        </>
    );
}

export function OtherLocations({
    locations,
    shownLabel,
}: {
    locations: readonly ProviderLocationInput[];
    shownLabel: string;
}) {
    const summary = summarizeProviderLocations(locations);
    if (!summary) return null;
    if (!shownLabel) {
        return (
            <div className="mt-3">
                <LocationsColumn summary={summary} />
            </div>
        );
    }
    return <AlsoIn summary={summary} shownLabel={shownLabel} />;
}
